"use client";

import { Link } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { ArrowLeft } from "lucide-react";

/**
 * Chrome Web Store requires a dedicated, extension-scoped privacy URL that
 * spells out: what data is collected, why, who it's shared with, how users
 * can revoke. Kept intentionally tight and scannable — reviewers bounce off
 * legalese walls.
 */
export default function ExtensionPrivacyPage() {
  const { locale } = useTranslation();
  const isFr = locale === "fr";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link href="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link href="/extension">
              <Button variant="ghost" size="sm" className="gap-1.5">
                <ArrowLeft className="h-4 w-4" />
                {isFr ? "Extension" : "Extension"}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <article className="mx-auto max-w-3xl">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            {isFr
              ? "Politique de confidentialité — Extension Chrome"
              : "Privacy policy — Chrome extension"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isFr ? "Dernière mise à jour : " : "Last updated: "}
            {new Date().toISOString().slice(0, 10)}
          </p>
          <p className="mt-6 text-muted-foreground">
            {isFr
              ? "Cette page décrit exactement quelles données l'extension Chrome Velyq collecte, pourquoi, et comment tu peux les contrôler. Elle complète la politique générale de Velyq disponible sur"
              : "This page explains exactly what data the Velyq Chrome extension collects, why, and how you can control it. It complements the main Velyq privacy policy at"}{" "}
            <Link href="/privacy" className="text-primary underline">
              {isFr ? "notre politique principale" : "our main policy"}
            </Link>
            .
          </p>

          <Section
            title={isFr ? "1. Ce que l'extension collecte" : "1. What the extension collects"}
          >
            <ul className="list-disc space-y-2 pl-5">
              <li>
                {isFr
                  ? "Le contenu textuel de la page d'offre active uniquement au moment où tu cliques « Ajouter à Velyq » (titre, entreprise, lieu, description, URL source). Rien n'est lu en arrière-plan."
                  : "The text content of the active job offer page, only at the moment you click \"Add to Velyq\" (title, company, location, description, source URL). Nothing is read in the background."}
              </li>
              <li>
                {isFr
                  ? "Tes tokens d'authentification Velyq (access + refresh), stockés localement dans chrome.storage.local sur ton appareil. Jamais synchronisés vers un autre appareil."
                  : "Your Velyq auth tokens (access + refresh), stored locally in chrome.storage.local on your device. Never synced across devices."}
              </li>
              <li>
                {isFr
                  ? "Quand tu utilises l'autofill Pro : les données de ton profil Velyq (nom, email, téléphone, LinkedIn, CV principal, lettre sélectionnée) sont envoyées au formulaire de candidature que tu es en train de remplir."
                  : "When you use Pro autofill: your Velyq profile data (name, email, phone, LinkedIn, primary CV, selected letter) is injected into the application form you're filling."}
              </li>
            </ul>
          </Section>

          <Section
            title={isFr ? "2. Ce que l'extension NE collecte PAS" : "2. What the extension does NOT collect"}
          >
            <ul className="list-disc space-y-2 pl-5">
              <li>
                {isFr
                  ? "Aucun scraping automatique. Aucun crawl en arrière-plan sur LinkedIn, Indeed ou ailleurs."
                  : "No automatic scraping. No background crawling on LinkedIn, Indeed or anywhere else."}
              </li>
              <li>
                {isFr
                  ? "Aucune donnée de navigation (historique, sites visités, clics) en dehors des pages d'offres que tu clippes activement."
                  : "No browsing data (history, visited sites, clicks) outside of the offer pages you actively clip."}
              </li>
              <li>
                {isFr
                  ? "Aucune information sur les recruteurs, collègues ou profils tiers."
                  : "No information about recruiters, colleagues or third-party profiles."}
              </li>
              <li>
                {isFr
                  ? "Aucune revente à des annonceurs, data brokers ou partenaires marketing."
                  : "No resale to advertisers, data brokers or marketing partners."}
              </li>
            </ul>
          </Section>

          <Section
            title={isFr ? "3. Permissions Chrome demandées" : "3. Chrome permissions requested"}
          >
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <b>storage</b> —{" "}
                {isFr
                  ? "stocker tes tokens d'auth et tes préférences localement."
                  : "store auth tokens and preferences locally."}
              </li>
              <li>
                <b>activeTab</b> —{" "}
                {isFr
                  ? "lire le contenu de l'onglet actif uniquement quand tu cliques sur l'icône de l'extension."
                  : "read the active tab's content only when you click the extension icon."}
              </li>
              <li>
                <b>scripting</b> —{" "}
                {isFr
                  ? "injecter l'autofill dans le formulaire de candidature que tu veux remplir."
                  : "inject the autofill script into the application form you want to fill."}
              </li>
              <li>
                <b>alarms</b> —{" "}
                {isFr
                  ? "programmer le rafraîchissement périodique des tokens et le scan des relances."
                  : "schedule periodic token refresh and reminder scans."}
              </li>
              <li>
                <b>identity</b> —{" "}
                {isFr
                  ? "lancer le flow OAuth vers velyq.com pour te connecter."
                  : "launch the OAuth flow to velyq.com for sign-in."}
              </li>
              <li>
                <b>notifications</b> —{" "}
                {isFr
                  ? "afficher les rappels Chrome pour tes relances et deadlines."
                  : "show Chrome notifications for your follow-ups and deadlines."}
              </li>
              <li>
                <b>host_permissions</b> —{" "}
                {isFr
                  ? "limité aux sites d'offres supportés (LinkedIn, Indeed, WTTJ, APEC, France Travail, Glassdoor), aux ATS supportés (Greenhouse, Lever, SmartRecruiters, Workday) et à velyq.com."
                  : "limited to supported job sites (LinkedIn, Indeed, WTTJ, APEC, France Travail, Glassdoor), supported ATS (Greenhouse, Lever, SmartRecruiters, Workday) and velyq.com."}
              </li>
            </ul>
          </Section>

          <Section title={isFr ? "4. Transferts et hébergement" : "4. Transfers and hosting"}>
            <p>
              {isFr
                ? "Tes données sont transmises à l'API Velyq (api.velyq.com) en HTTPS. Elles sont hébergées sur Google Cloud (europe-west1, France) et traitées par notre backend FastAPI. Aucun transfert hors UE pour le stockage primaire."
                : "Your data is transmitted to the Velyq API (api.velyq.com) over HTTPS. It's hosted on Google Cloud (europe-west1, France) and processed by our FastAPI backend. No primary storage outside the EU."}
            </p>
            <p className="mt-3">
              {isFr
                ? "Les modèles de langage utilisés pour le parsing d'offres et la génération de lettres (OpenAI, Google Gemini) peuvent recevoir le texte de l'offre en tant que sous-traitants. Aucun donnée personnelle identifiante n'est incluse dans ces appels au-delà de ce qui est visible sur la page publique."
                : "The LLMs used for offer parsing and letter generation (OpenAI, Google Gemini) may receive the offer text as sub-processors. No personally identifying data beyond what's on the public page is included in those calls."}
            </p>
          </Section>

          <Section title={isFr ? "5. Tes droits" : "5. Your rights"}>
            <p>
              {isFr
                ? "Conformément au RGPD, tu disposes des droits d'accès, de rectification, d'effacement, de portabilité et de limitation sur tes données. Tu peux :"
                : "Under GDPR you have rights of access, rectification, erasure, portability and restriction on your data. You can:"}
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>
                {isFr
                  ? "Révoquer l'accès de l'extension depuis "
                  : "Revoke extension access from "}
                <Link
                  href="/settings/extension"
                  className="text-primary underline"
                >
                  {isFr ? "Réglages → Extension" : "Settings → Extension"}
                </Link>
                {isFr ? " (invalidation immédiate des tokens)." : " (immediate token invalidation)."}
              </li>
              <li>
                {isFr
                  ? "Supprimer l'extension : clic droit sur l'icône → « Supprimer de Chrome »."
                  : "Uninstall the extension: right-click the icon → \"Remove from Chrome\"."}
              </li>
              <li>
                {isFr
                  ? "Supprimer ton compte Velyq et toutes tes données associées depuis Réglages → Compte."
                  : "Delete your Velyq account and all associated data from Settings → Account."}
              </li>
              <li>
                {isFr
                  ? "Nous contacter à "
                  : "Contact us at "}
                <a
                  href="mailto:contact@velyq.com"
                  className="text-primary underline"
                >
                  contact@velyq.com
                </a>
                {isFr ? " pour toute question ou demande RGPD." : " for any question or GDPR request."}
              </li>
            </ul>
          </Section>

          <Section
            title={isFr ? "6. Conservation des données" : "6. Data retention"}
          >
            <p>
              {isFr
                ? "Les candidatures que tu clippes restent dans ton tracker aussi longtemps que tu le souhaites. Elles sont supprimées immédiatement et définitivement quand tu supprimes une candidature ou ton compte. Les tokens de l'extension expirent automatiquement (24h pour l'access token, 7 jours pour le refresh) et sont effacés à la déconnexion."
                : "Offers you clip remain in your tracker as long as you want. They're deleted immediately and permanently when you remove an application or your account. Extension tokens expire automatically (24h for access, 7 days for refresh) and are wiped on sign-out."}
            </p>
          </Section>

          <Section title={isFr ? "7. Contact" : "7. Contact"}>
            <p>
              {isFr
                ? "Responsable de traitement : Mathieu Neumar (Velyq), entrepreneur individuel · "
                : "Data controller: Mathieu Neumar (Velyq), sole trader · "}
              <a
                href="mailto:contact@velyq.com"
                className="text-primary underline"
              >
                contact@velyq.com
              </a>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {isFr
                ? "Cette politique peut évoluer. Chaque changement matériel sera annoncé via la page /changelog et par email si tu as un compte Velyq."
                : "This policy may evolve. Every material change is announced via /changelog and by email if you have a Velyq account."}
            </p>
          </Section>
        </article>
      </main>

      <Footer />
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] text-foreground/90 leading-relaxed">
        {children}
      </div>
    </section>
  );
}
